import React from "react";
import { useIsMobile } from "../../../../hooks/useIsMobile";

interface AuthorInfo {
    followerCount: number;
    keyword1: string;
    nickname: string;
    profilePhoto?: string;
    totalLikeCount: number;
    mbti?: string;
    writingGenre?: string;
}

interface UserInfo {
    likeCount: number;
    followedAuthorCount: number;
    nickname: string;
    profilePhoto?: string;
}

interface ProfileSectionProps {
    isAuthor: boolean;
    profile: AuthorInfo | UserInfo;
    onEdit?: () => void;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ isAuthor, profile, onEdit }) => {
    const isMobile = useIsMobile();

    const profileImg = profile.profilePhoto && (
        <img
            src={`data:image/png;base64,${profile.profilePhoto}`}
            className="object-cover w-full h-full rounded-full"
            alt="Profile"
        />
    );

    if (isMobile) {
        if (!isAuthor) {
            // 일반 사용자의 모바일 전용 단순화 레이아웃
            return (
                <div className="flex flex-col items-center gap-3 py-2 text-center w-full">
                    {/* 닉네임 */}
                    <span className="text-[#222] text-[24px] font-bold leading-none">{profile.nickname}</span>

                    {/* 닉네임 밑에 좋아요, 팔로우 수치 수평 정렬 */}
                    <div className="flex flex-row items-center justify-center gap-4 mt-1 text-[#666] text-[13.5px]">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[#888]">좋아요</span>
                            <span className="text-[#222] font-bold">{(profile as UserInfo).likeCount}</span>
                        </div>
                        <div className="w-px h-3 bg-[#e0e0e0]" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-[#888]">팔로우</span>
                            <span className="text-[#222] font-bold">{(profile as UserInfo).followedAuthorCount}</span>
                        </div>
                    </div>
                </div>
            );
        }

        // 작가 사용자의 기존 모바일 레이아웃 (보존)
        return (
            <div className="flex flex-col items-center gap-5">
                {/* 프로필 사진 */}
                <div className="w-[110px] h-[110px] rounded-full p-[3px] bg-white shadow-[0px_8px_15px_rgba(25,207,122,0.15)]">
                    <div className="w-full h-full rounded-full overflow-hidden">
                        {profileImg}
                    </div>
                </div>

                {/* 이름 + 버튼 */}
                <div className="flex flex-col items-center gap-2 text-center">
                    <span className="text-[#222] text-[24px] font-bold leading-none">{profile.nickname}</span>
                    {onEdit && (
                        <button
                            className="border border-[#e0e0e0] text-[#666] text-[12px] bg-white font-medium px-3 py-1.5 rounded-full hover:border-[#19cf7a] hover:text-[#19cf7a] transition-colors mt-0.5"
                            onClick={onEdit}
                        >
                            프로필 수정
                        </button>
                    )}

                    {isAuthor && (profile as AuthorInfo).keyword1 && (
                        <p className="font-namsan text-[#888] text-[14px] mt-1">
                            주로{" "}
                            <span className="text-[#19cf7a] font-bold text-[16px]">
                                {(profile as AuthorInfo).keyword1}
                            </span>{" "}
                            글을 써요.
                        </p>
                    )}

                    {/* MBTI + 장르 태그 */}
                    {isAuthor && ((profile as AuthorInfo).mbti || (profile as AuthorInfo).writingGenre) && (
                        <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                            {(profile as AuthorInfo).mbti && (
                                <span className="bg-[#f3f4f6] border border-[#e5e7eb] text-[#4b5563] text-[11px] rounded-full px-2.5 py-0.5">
                                    {(profile as AuthorInfo).mbti}
                                </span>
                            )}
                            {(profile as AuthorInfo).writingGenre &&
                                (profile as AuthorInfo).writingGenre!
                                    .split(',').map(g => g.trim()).filter(Boolean)
                                    .map(g => (
                                        <span key={g} className="bg-[#f3f4f6] border border-[#e5e7eb] text-[#4b5563] text-[11px] rounded-full px-2.5 py-0.5">
                                            {g}
                                        </span>
                                    ))
                            }
                        </div>
                    )}
                </div>

                {/* 통계 */}
                <div className="flex flex-row items-center gap-6">
                    <div className="flex flex-col items-center gap-1">
                        <span className="text-[#888] text-[12px]">팔로워</span>
                        <span className="text-[#222] text-[18px] font-bold">{(profile as AuthorInfo).followerCount}</span>
                    </div>
                    <div className="w-px h-9 bg-[#e0e0e0]" />
                    <div className="flex flex-col items-center gap-1">
                        <span className="text-[#888] text-[12px]">좋아요</span>
                        <span className="text-[#222] text-[18px] font-bold">{(profile as AuthorInfo).totalLikeCount}</span>
                    </div>
                </div>
            </div>
        );
    }

    /* Desktop */
    if (!isAuthor) {
        // 일반 사용자의 데스크톱 전용 단순화 레이아웃
        return (
            <div className="flex flex-col items-start gap-4 py-3 w-full">
                {/* 닉네임 */}
                <span className="text-[#222] text-[32px] font-bold leading-none">{profile.nickname}</span>

                {/* 닉네임 밑에 좋아요, 팔로우 수치 수평 정렬 */}
                <div className="flex flex-row items-center gap-5 mt-1 text-[#555] text-[15px]">
                    <div className="flex items-center gap-2">
                        <span className="text-[#888]">좋아요</span>
                        <span className="text-[#222] font-bold text-[18px]">{(profile as UserInfo).likeCount}</span>
                    </div>
                    <div className="w-px h-4 bg-[#e0e0e0]" />
                    <div className="flex items-center gap-2">
                        <span className="text-[#888]">팔로우</span>
                        <span className="text-[#222] font-bold text-[18px]">{(profile as UserInfo).followedAuthorCount}</span>
                    </div>
                </div>
            </div>
        );
    }

    // 작가 사용자의 기존 데스크톱 레이아웃 (보존)
    return (
        <div className="flex flex-row items-center gap-10 w-full">
            {/* 프로필 사진 */}
            <div className="w-[130px] h-[130px] rounded-full p-[4px] bg-white shadow-[0px_8px_15px_rgba(25,207,122,0.15)] flex-shrink-0">
                <div className="w-full h-full rounded-full overflow-hidden">
                    {profileImg}
                </div>
            </div>

            {/* 이름 + 키워드 + 버튼 */}
            <div className="flex flex-col items-start gap-3 flex-1">
                <div className="flex items-center gap-3">
                    <span className="text-[#222] text-[28px] font-bold leading-none">{profile.nickname}</span>
                    {onEdit && (
                        <button
                            className="border border-[#e0e0e0] text-[#666] bg-white text-[13px] font-medium px-4 py-1.5 rounded-full hover:border-[#19cf7a] hover:text-[#19cf7a] transition-colors"
                            onClick={onEdit}
                        >
                            프로필 수정
                        </button>
                    )}
                </div>

                {(profile as AuthorInfo).keyword1 && (
                    <p className="font-namsan text-[#666] text-[17px]">
                        주로{" "}
                        <span className="text-[#19cf7a] font-bold text-[20px]">
                            {(profile as AuthorInfo).keyword1}
                        </span>{" "}
                        글을 써요.
                    </p>
                )}

                {/* MBTI + 장르 태그 */}
                {((profile as AuthorInfo).mbti || (profile as AuthorInfo).writingGenre) && (
                    <div className="flex flex-wrap gap-2">
                        {(profile as AuthorInfo).mbti && (
                            <span className="bg-[#f3f4f6] border border-[#e5e7eb] text-[#4b5563] text-[12px] rounded-full px-3 py-1">
                                {(profile as AuthorInfo).mbti}
                            </span>
                        )}
                        {(profile as AuthorInfo).writingGenre &&
                            (profile as AuthorInfo).writingGenre!
                                .split(',').map(g => g.trim()).filter(Boolean)
                                .map(g => (
                                    <span key={g} className="bg-[#f3f4f6] border border-[#e5e7eb] text-[#4b5563] text-[12px] rounded-full px-3 py-1">
                                        {g}
                                    </span>
                                ))
                        }
                    </div>
                )}
            </div>

            {/* 통계 */}
            <div className="flex flex-row items-center gap-6 flex-shrink-0">
                <div className="flex flex-col items-center gap-1">
                    <span className="text-[#888] text-[14px]">팔로워</span>
                    <span className="text-[#222] text-[22px] font-bold">{(profile as AuthorInfo).followerCount}</span>
                </div>
                <div className="w-px h-9 bg-[#e0e0e0]" />
                <div className="flex flex-col items-center gap-1">
                    <span className="text-[#888] text-[14px]">좋아요</span>
                    <span className="text-[#222] text-[22px] font-bold">{(profile as AuthorInfo).totalLikeCount}</span>
                </div>
            </div>
        </div>
    );
};

export default ProfileSection;
